import MicPermissionGate from "./MicPermissionGate";
import type { TourConfig } from "./types";

/** Selector that only exists when document generation is NOT in progress. */
const DOC_TOOLBAR = '[data-tour="doc-toolbar"]';

export const quickStartTour: TourConfig = {
    id: "quick-start",
    name: "Quick Start",
    description: "Record, generate, and see your first medical record",
    showInHelpMenu: true,
    steps: [
        // --- Intro ---
        {
            placement: "center",
            scoutState: "wave",
            title: "Quick Start",
            content: "Let's make a recording and generate your first medical record. It only takes a minute!",
            route: "/",
        },

        // --- Mic permission gate ---
        {
            placement: "center",
            scoutState: "point",
            title: "Microphone Check",
            content: <MicPermissionGate />,
            route: "/",
        },

        // --- Record ---
        {
            target: '[data-tour="record-button"]',
            placement: "right",
            scoutState: "point",
            title: "Start Recording",
            content: "Tap the record button to begin.",
            spotlightClicks: true,
            actionLabel: "Tap Record to start",
        },
        {
            target: "#recording-widget-stop-button",
            placement: "right",
            scoutState: "point",
            title: "Say Something",
            content:
                "Say: \"Hi, I'm Dr. Smith and today we're seeing Fluffy for a routine wellness exam. Everything looks good, we'll just need to update vaccines and run some bloodwork.\" Then tap stop.",
            spotlightClicks: true,
            waitForTarget: true,
            actionLabel: "Tap Stop when you're done",
        },

        // --- Select templates ---
        {
            target: '[data-tour="widget-document-select"]',
            placement: "left",
            scoutState: "point",
            title: "Choose Your Templates",
            content: "Select which document templates to generate from your recording.",
            spotlightClicks: true,
            waitForTarget: true,
            enableNextSelector: '[data-tour="recording-widget-generate"]:not(:disabled)',
        },

        // --- Generate ---
        {
            target: '[data-tour="recording-widget-actions"]',
            placement: "top",
            scoutState: "point",
            title: "Generate Your Documents",
            content: "Tap Generate to turn your recording into medical records.",
            spotlightClicks: true,
            waitForTarget: true,
            actionLabel: "Tap Generate",
        },

        // --- Open visit ---
        {
            target: '[data-tour="recording-widget-visit"]',
            placement: "top",
            scoutState: "point",
            title: "Open Your Visit",
            content: "Tap Visit to see your documents being created.",
            spotlightClicks: true,
            waitForTarget: true,
            actionLabel: "Tap Visit to open it",
        },

        // --- Processing audio ---
        {
            target: '[data-tour="clip-processing"]',
            placement: "left",
            scoutState: "point",
            title: "Processing Audio",
            content: "We're extracting the conversation from your recording. Just a few seconds.",
            waitForTarget: true,
            waitForTargetTimeout: 15000,
            advanceWhenSelector: '[data-tour="loading-generating"], [data-tour="doc-toolbar"]',
            onTimeoutGroup: "recovery",
            bailoutSelector: "[data-tour-asset-failed]",
        },

        // --- Generating documents ---
        {
            target: '[data-tour="loading-generating"]',
            placement: "bottom",
            scoutState: "point",
            title: "Writing Your Documents",
            content: "HappyDoc is turning your conversation into medical records. Almost there!",
            waitForTarget: true,
            waitForTargetTimeout: 30000,
            advanceWhenSelector: '[data-tour="doc-toolbar"]',
            onTimeoutGroup: "recovery",
            bailoutSelector: "[data-tour-asset-failed]",
        },

        // --- Documents ready! ---
        {
            target: DOC_TOOLBAR,
            placement: "bottom",
            scoutState: "celebrate",
            title: "Your Documents Are Ready!",
            content:
                "That's it! Your medical records have been generated from your recording. You can regenerate, copy, print, or email any document from here.",
            waitForTarget: true,
            waitForTargetTimeout: 10000,
            onTimeoutGroup: "recovery",
            bailoutSelector: "[data-tour-asset-failed]",
        },
    ],

    groups: {
        recovery: [
            {
                target: "#in-line-start-recording",
                placement: "left",
                scoutState: "point",
                title: "Let's Try Again",
                content:
                    "It looks like the recording didn't pick up any audio. Tap Record to try again — speak clearly into your microphone.",
                spotlightClicks: true,
                waitForTarget: true,
                actionLabel: "Tap Record",
            },
            {
                target: "#recording-widget-stop-button",
                placement: "right",
                scoutState: "point",
                title: "Record & Stop",
                content:
                    "Say: \"Hi, I'm Dr. Smith and today we're seeing Fluffy for a routine wellness exam. Everything looks good, we'll just need to update vaccines and run some bloodwork.\" Then tap stop.",
                spotlightClicks: true,
                waitForTarget: true,
                actionLabel: "Tap Stop when you're done",
            },
            {
                target: '[data-tour="widget-document-select"]',
                placement: "left",
                scoutState: "point",
                title: "Choose Your Templates",
                content: "Select which document templates to generate, then continue.",
                spotlightClicks: true,
                waitForTarget: true,
                enableNextSelector: '[data-tour="recording-widget-generate"]:not(:disabled)',
            },
            {
                target: '[data-tour="recording-widget-actions"]',
                placement: "top",
                scoutState: "point",
                title: "Generate",
                content: "Tap Generate to create your documents.",
                spotlightClicks: true,
                waitForTarget: true,
                actionLabel: "Tap Generate",
            },
            {
                target: DOC_TOOLBAR,
                placement: "bottom",
                scoutState: "celebrate",
                title: "Your Documents Are Ready!",
                content: "Great — your documents are generated!",
                waitForTarget: true,
                waitForTargetTimeout: 25000,
                onTimeoutGroup: "mic-help",
                bailoutSelector: "[data-tour-asset-failed]",
            },
        ],

        "mic-help": [
            {
                placement: "center",
                scoutState: "wave",
                title: "Check Your Microphone",
                content:
                    "We weren't able to detect any audio. Please check that your microphone is connected and that HappyDoc has microphone permissions in your browser. Restart this tour from the Help menu once your mic is working!",
                endsTour: true,
            },
        ],
    },
};
